import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { useDrawerInsetPanel } from "../../components/ui/drawer";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { Text } from "../../components/ui/text";
import * as React from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  isItemCollapsed,
  subsectionKey,
  useEntitySelector,
  type EntityItemConfig,
} from "./entity-selector-context";
import {
  EntitySelectorItem,
  type EntitySelectorRowRhythm,
} from "./entity-selector-item";
import {
  EntitySelectorSectionLabel,
  type EntitySelectorSectionLabelAction,
} from "./entity-selector-layout";
import { useListReorder } from "./use-list-reorder";
import { useMediaQuery } from "../../hooks/use-media-query";
import type { Omittable } from "../../lib/props";

const EMPTY_ROWS: EntityItemConfig[] = [];

/** A row's height on each rhythm (see `EntitySelectorRowRhythm`): the 24px
 *  visual rail plus its padding. A windowed list is sized from it, and its
 *  placeholders stand at it, so a page landing never changes the list's
 *  height. Spacious is the one rung that moves: 40px on a phone, 36px from
 *  `sm` up, as its padding does. */
const RHYTHM_ROW_HEIGHT: Record<EntitySelectorRowRhythm, number> = {
  compact: 28,
  default: 32,
  spacious: 40,
};
const SPACIOUS_ROW_HEIGHT_SM = 36;

// Rows mounted past each edge of a windowed list's viewport.
const WINDOW_OVERSCAN = 10;

export type EntitySelectorPoolProps = Omittable<{
  value: string;
  label?: string;
  onItemAction?: (entity: EntityItemConfig) => void;
  hideSelectionHighlight?: boolean;
  /**
   * Drop the rows' selection MARKS — the multi-select checkbox and the
   * single-select trailing check — for a picker that states the selection
   * elsewhere: tokens in the search field, the value on the trigger. The
   * rows stay the controls; only the redundant restatement goes. Pair with
   * `hideSelectionHighlight` to drop the wash too. @default false
   */
  hideSelectionMarks?: boolean;
  /**
   * Skip DRAWING the pool's section label. For a one-pool popup whose search
   * field (or whose tokens) already names what the rows are. The label still
   * names the listbox for assistive tech — a list that is obvious to the eye
   * is not obvious to a screen reader. @default false
   */
  hideLabel?: boolean;
  /**
   * A trailing text action on the pool's section label — the door to where
   * these rows are managed ("Edit"). Renders only with the label, so it is
   * dropped with `hideLabel` and on a sectioned list, whose group headings are the
   * sections' own.
   */
  labelAction?: EntitySelectorSectionLabelAction;
  /**
   * Put a bulk toggle on the pool's label rung: "Select all" while any listed
   * row is unchosen, "Clear" once every one is. "All" means the rows on
   * screen — a typed query narrows it to the matches — and skips disabled
   * rows. It needs the label rung, so it is inert where that rung is not
   * drawn (`hideLabel`, a sectioned list) and on a selector that caps or
   * single-selects, where "all" would lie. `labelAction` takes the slot when
   * both are given. @default false
   */
  selectAll?: boolean;
  /**
   * How the pool's own scroller reserves the scrollbar's lane. `true` (the
   * default) reserves it only while the list overflows; `"stable"` reserves
   * it always, for a host that supplies no right gutter of its own and uses
   * the lane as the list's right inset (the entity picker's dialog).
   */
  scrollbarGutter?: boolean | "stable";
  /**
   * The rows' vertical rhythm. Unset, a flat list stands on the 32px rung and a
   * sectioned list takes the roomier `"spacious"` rhythm, since rows packed as
   * tightly as the group headings above them stop reading as groups. Set it to
   * override that default: `"spacious"` for a pool mounted at the size of a
   * page (the entity picker), where the 32px rung reads as cramped; `"compact"`
   * (28px) for a short sectioned list of glyph-led rows (a stage picker), where
   * the group headings already separate the groups and roomier rows only push the
   * list past the popup.
   */
  rhythm?: EntitySelectorRowRhythm;
}>;

type EntitySectionHeadingProps = Omittable<{
  label: string;
  collapsed?: boolean;
  onToggle?: () => void;
  /** A `subsection` heading — indented under its section and cased naturally
   *  (rather than upper) so the two levels are distinguishable at a glance. */
  nested?: boolean;
}>;

// The group heading's own content, shared by the static and the pressable spelling so
// the two cannot drift apart.
//
// It carries NO item count. A count answers "how much is hidden behind this
// heading", which only matters if a fold is the normal resting state — and it
// is not: sections start open, so the number is redundant beside the rows it is
// counting, and it competes with the label for the eye on every heading in the
// list to serve the collapsed minority.
function EntitySectionHeadingContent({
  label,
  collapsed,
  onToggle,
  nested = false,
}: EntitySectionHeadingProps): React.ReactElement {
  return (
    <>
      {onToggle ? (
        <Icon
          color="muted"
          name={collapsed ? "chevron-right" : "chevron-down"}
          size="xs"
        />
      ) : null}
      <Text size="xs" variant="muted" weight="semibold">
        {nested ? label : label.toUpperCase()}
      </Text>
    </>
  );
}

// In-list group heading above a run of same-`section` items. Smaller and quieter
// than the pool-label header (xs semibold muted, uppercased here so callers
// pass natural case) — it subdivides one list rather than titling it.
//
// With `onToggle` it becomes a pressable disclosure: a real `button`, so it is
// focusable and answers Enter/Space with no extra handlers, carrying
// `aria-expanded` and a leading chevron that follows `collapsed`. The whole row
// is the hit target. Without one it renders a plain static group heading.
function EntitySectionHeading(
  props: EntitySectionHeadingProps,
): React.ReactElement {
  const { collapsed, onToggle, nested = false } = props;
  // Even padding above and below. A group heading with 8px over it and 2px under it
  // sits nearer the rows it labels than the group it separates from, which is
  // backwards — a heading belongs to what follows it, but it still has to look
  // detached from what came before.
  const padding = {
    // The list's 10px reading edge; a nested heading starts where its
    // subsection rows do, one 8px indent further in.
    className: nested ? "pr-2.5 pl-4.5" : "px-2.5",
    paddingTop: nested ? "xs" : "sm",
    paddingBottom: nested ? "2xs" : "sm",
  } as const;

  if (!onToggle) {
    return (
      <Box align="center" direction="row" gap="xs" {...padding}>
        <EntitySectionHeadingContent {...props} />
      </Box>
    );
  }

  return (
    <Box
      align="center"
      aria-expanded={!collapsed}
      as="button"
      cursor="pointer"
      direction="row"
      gap="xs"
      justify="start"
      onClick={onToggle}
      type="button"
      width="100%"
      {...padding}
    >
      <EntitySectionHeadingContent {...props} />
    </Box>
  );
}

// The collapse controls a collapsible pool threads into its headings. Keys are
// the section label, or `subsectionKey(section, subsection)` for the nested level.
type CollapseApi = {
  isCollapsed: (key: string) => boolean;
  toggle: (key: string) => void;
};

// The group heading(s) that precede row `index`: the section heading when the
// section changes here, and the subsection heading when the subsection changes
// (both fire on the first row of a new section that also has a subsection).
// Returns null for every other row. `collapse` is present only for a collapsible
// pool and makes each heading a disclosure.
function sectionHeadingFor(
  data: readonly (EntityItemConfig | null)[],
  index: number,
  collapse?: CollapseApi,
  style: "section-label" | "label" = "section-label",
  rules = false,
  firstTrailing?: React.ReactNode,
): React.ReactNode {
  const item = data[index];
  if (!item?.section) return null;
  const prev = data[index - 1];
  const section = item.section;
  const startsSection = section !== prev?.section;
  // The first heading of the list — whichever run leads — carries the
  // list's trailing control, so it never depends on one section existing.
  const isFirstHeading =
    startsSection && !data.slice(0, index).some((earlier) => earlier?.section);
  const sub = item.subsection;
  const startsSub = !!sub && (startsSection || sub !== prev?.subsection);
  if (!startsSection && !startsSub) return null;

  // A collapsed SECTION swallows its subsection headings too — only its own
  // heading stays, as the control that reopens it.
  const sectionClosed = !!collapse?.isCollapsed(section);
  return (
    <>
      {/* A rule BETWEEN groups, never above the first one. Padding alone stopped
          reading as a group boundary once several sections stacked up: the eye
          needs a line to see where one group ends, not just more air. The
          `label` style is the exception: one named run inside one vocabulary
          is set off by the label rung's own air, the way the pool label sits
          under the search field. */}
      {startsSection && index > 0 && (style === "section-label" || rules) ? (
        <Separator className="-mx-1 my-1.5" />
      ) : null}
      {startsSection && style === "label" ? (
        <Box paddingTop={index > 0 && !rules ? "xs" : undefined}>
          <EntitySelectorSectionLabel
            label={section}
            trailing={isFirstHeading ? firstTrailing : undefined}
          />
        </Box>
      ) : startsSection ? (
        collapse ? (
          <EntitySectionHeading
            collapsed={sectionClosed}
            label={section}
            onToggle={() => collapse.toggle(section)}
          />
        ) : (
          <EntitySectionHeading label={section} />
        )
      ) : null}
      {startsSub && !sectionClosed ? (
        collapse ? (
          <EntitySectionHeading
            collapsed={collapse.isCollapsed(subsectionKey(section, sub))}
            label={sub}
            nested
            onToggle={() => collapse.toggle(subsectionKey(section, sub))}
          />
        ) : (
          <EntitySectionHeading label={sub} nested />
        )
      ) : null}
    </>
  );
}

function PlaceholderRow({ height }: { height: number }) {
  return (
    <Box
      data-slot="entity-selector-placeholder"
      direction="row"
      align="center"
      gap="sm"
      className="px-2.5"
      height={height}
    >
      {/* Skeleton bars are decorative fills, not surfaces — a 24px dot has
          no elevation to carry. */}
      <Box rounded="full" background="muted" width={24} height={24} />
      <Box rounded="sm" background="muted" width={96} height={12} />
    </Box>
  );
}

function EntitySelectorPool({
  value,
  label,
  onItemAction,
  hideSelectionHighlight = false,
  hideSelectionMarks = false,
  hideLabel = false,
  labelAction,
  selectAll = false,
  scrollbarGutter = true,
  rhythm,
}: EntitySelectorPoolProps) {
  const {
    config,
    variant,
    activePool,
    getPool,
    getPoolBadge,
    getFilteredItems,
    getPinnedItems,
    getOmittedCount,
    isSectionCollapsed,
    toggleSection,
    search,
    activeId,
    activeIndex,
    cursorVisible,
    setActiveIndex,
    isActiveFromPointer,
    onOpenChange,
    isSelected,
    selectItems,
    deselectItems,
    registerSelectedHost,
  } = useEntitySelector();

  // The selected section's seat: `EntitySelectorSelected` portals its rows
  // here, at the top of this pool's scroller, so chosen and unchosen rows are
  // ONE list under one scrollbar. Registered by pool id — a tabbed selector
  // seats it in whichever pool is showing.
  const selectedHostRef = React.useCallback(
    (node: HTMLDivElement | null) => registerSelectedHost(value, node),
    [registerSelectedHost, value],
  );

  // The row wash paints only a keyboard-driven cursor; the id itself stays
  // live underneath for the open-on-selection scroll below.
  const paintedActiveId = cursorVisible ? activeId : null;

  const pool = getPool(value);
  const badge = getPoolBadge(value);
  const filteredItems = getFilteredItems(value);
  const sectionLabel = label ?? pool?.label;
  const isActive = activePool === value;
  const hideAvatars = pool?.hideAvatars ?? false;

  // A badge marked closeOnAction is a door: pressing it hands the interaction
  // to whatever it opens, so the picker dismisses first — the same order the
  // selection auto-close uses. The list's own handler wins over the badge's
  // pool-carried default.
  const baseItemAction = onItemAction ?? badge?.onAction;
  const handleItemAction = React.useMemo(() => {
    if (!baseItemAction || !badge?.closeOnAction) return baseItemAction;
    const action = baseItemAction;
    return (entity: EntityItemConfig) => {
      onOpenChange(false);
      action(entity);
    };
  }, [badge?.closeOnAction, baseItemAction, onOpenChange]);

  // Pinned rows sit above the section header, outside the (possibly virtual)
  // list below. Sourced from the root's cache — the same list backs keyboard
  // navigation, where pinned rows occupy indices 0..n-1 ahead of the
  // regular items.
  const pinnedItems = getPinnedItems(value);
  const pinnedRuns = React.useMemo(() => {
    const runs: { section: string | undefined; start: number; items: EntityItemConfig[] }[] = [];
    pinnedItems.forEach((item, index) => {
      const last = runs[runs.length - 1];
      if (last && last.section === item.section) last.items.push(item);
      else runs.push({ section: item.section, start: index, items: [item] });
    });
    return runs;
  }, [pinnedItems]);

  // Loaded rows a selected section above is listing instead of this pool (see
  // `registerSelectedSection` on the context). They are still counted in a virtual
  // pool's `total`, so the virtual math below takes them back out.
  const omitted = getOmittedCount(value);
  const omittedRef = React.useRef(omitted);
  omittedRef.current = omitted;

  // Drawer mode — and an inline mount inside a phone sheet's panel (a
  // responsive popover's, a submenu sheet's, a table cell's) — the sheet's
  // ScrollArea is the ONE scrollable, so the pool renders its rows flat and
  // hooks its scroll bookkeeping onto that ancestor viewport instead of
  // mounting its own ScrollArea (a nested scroller under a finger's swipe).
  const inSheet = useDrawerInsetPanel();
  const ownScroller = variant !== "drawer" && !inSheet;

  // The rows container. The actual scrolling element is resolved from it:
  // the pool's own ScrollArea viewport (popover/inline) or the enclosing
  // DrawerPanel viewport (drawer).
  const rowsRef = React.useRef<HTMLDivElement | null>(null);
  const [scroller, setScroller] = React.useState<HTMLElement | null>(null);

  const rowsCallbackRef = React.useCallback((node: HTMLDivElement | null) => {
    rowsRef.current = node;
    setScroller(
      node?.closest<HTMLElement>("[data-slot=scroll-area-viewport]") ?? null,
    );
  }, []);

  const isVirtual = pool?.total != null && pool.total > 0;
  const isSearching = search.trim().length > 0;

  // Reorder is an unfiltered-list gesture: a search result is not an order,
  // and virtual lists page rows in and out from under the drag.
  const reorderable = !!pool?.onReorder && !isSearching && !isVirtual;
  const gripStanding = pool?.gripVisibility === "always";
  const onReorderRef = React.useRef(pool?.onReorder);
  onReorderRef.current = pool?.onReorder;
  const handleReorderDrop = React.useCallback(
    (draggedId: string, beforeId: string | null) => {
      onReorderRef.current?.(draggedId, beforeId);
    },
    [],
  );
  const { draggingId, indicatorY } = useListReorder({
    rowsRef,
    enabled: reorderable,
    onDrop: handleReorderDrop,
  });

  // Windowed (a virtual pool, unsearched, without sections): one slot per row
  // of `total`, loaded rows from index 0 and placeholders after, of which only
  // the rows in view mount — so the list is its full height from the first
  // page and a page landing fills slots instead of growing it. Otherwise:
  // dense filtered items.
  const windowed =
    isVirtual && !isSearching && !filteredItems.some((item) => item.section);
  const data = React.useMemo(() => {
    if (windowed) {
      // `total` counts the omitted rows too; they are loaded and listed above,
      // so without the subtraction the tail would keep that many placeholders
      // for rows no fetch will ever fill.
      const total = Math.max(pool!.total! - omitted, filteredItems.length);
      const arr: (EntityItemConfig | null)[] = new Array(total).fill(null);
      for (const [i, item] of filteredItems.entries()) arr[i] = item;
      return arr;
    }
    return filteredItems;
  }, [windowed, pool, filteredItems, omitted]);
  const pointerLikely = useMediaQuery("sm");
  const windowRowHeight =
    rhythm === "spacious" && pointerLikely
      ? SPACIOUS_ROW_HEIGHT_SM
      : RHYTHM_ROW_HEIGHT[rhythm ?? "default"];

  // Where the rows start inside the scroller: the selected host and the
  // section label sit above them, inside the same scroll content, so it is
  // measured again whenever that content resizes.
  const [scrollMargin, setScrollMargin] = React.useState(0);
  React.useLayoutEffect(() => {
    const rowsEl = rowsRef.current;
    const content = rowsEl?.parentElement;
    if (!windowed || !scroller || !rowsEl || !content) return;
    const measure = () => {
      const next =
        rowsEl.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    for (const child of Array.from(content.children)) {
      if (child !== rowsEl) observer.observe(child);
    }
    return () => observer.disconnect();
  }, [windowed, scroller]);

  const virtualizer = useVirtualizer({
    count: windowed ? data.length : 0,
    getScrollElement: () => (windowed ? scroller : null),
    estimateSize: () => windowRowHeight,
    overscan: WINDOW_OVERSCAN,
    scrollMargin,
    enabled: windowed,
    // flushSync from a layout effect warns; React batches the update anyway.
    useFlushSync: false,
  });
  const virtualRows = windowed ? virtualizer.getVirtualItems() : [];
  const firstVirtual = virtualRows[0]?.index ?? -1;
  const lastVirtual = virtualRows[virtualRows.length - 1]?.index ?? -1;

  // ── Collapsible sections ────────────────────────────────────────────────
  // The state lives in the ROOT (see `isSectionCollapsed` on the context): a
  // folded section's rows are dropped from the keyboard-nav list, so the same
  // owner has to answer both "is this rendered" and "can the cursor reach it".
  // This binds that per-pool API to this pool's id.
  const collapseApi = React.useMemo<CollapseApi | undefined>(
    () =>
      pool?.collapsibleSections
        ? {
            isCollapsed: (key: string) => isSectionCollapsed(value, key),
            toggle: (key: string) => toggleSection(value, key),
          }
        : undefined,
    [pool?.collapsibleSections, value, isSectionCollapsed, toggleSection],
  );

  const onLoadRangeRef = React.useRef(pool?.onLoadRange);
  onLoadRangeRef.current = pool?.onLoadRange;

  // A windowed list asks for the pages under its mounted rows.
  React.useEffect(() => {
    if (!windowed || firstVirtual < 0) return;
    onLoadRangeRef.current?.(firstVirtual, lastVirtual + omittedRef.current);
  }, [windowed, firstVirtual, lastVirtual]);

  // Attach scroll listener for virtual loading (an unwindowed virtual list:
  // a searched or sectioned one)
  React.useEffect(() => {
    const el = scroller;
    if (!el || windowed) return;
    const handler = () => {
      if (!onLoadRangeRef.current) return;
      // Rows are NOT a fixed height: real items render at their natural height
      // (~26px) while skeletons are ITEM_HEIGHT (44px), and the list is not
      // windowed — every entry is a real DOM node packed from the top. Dividing
      // scrollTop by ITEM_HEIGHT therefore undershot the true index badly (at the
      // bottom it reported ~row 132 of 220), so onLoadRange kept asking for pages
      // the user had already scrolled past and never requested the pages for the
      // rows actually in view — the trailing rows stayed skeletons forever.
      // Derive the visible range from the real scroll geometry instead: the
      // fraction of total content scrolled maps directly to item indices,
      // regardless of per-row height.
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollHeight <= 0) return;
      const startIndex = Math.floor((scrollTop / scrollHeight) * data.length);
      const endIndex = Math.min(
        Math.ceil(((scrollTop + clientHeight) / scrollHeight) * data.length),
        data.length - 1,
      );
      // Indices here are positions in the DRAWN list, which is the loaded
      // prefix minus the rows the selected section took. The consumer pages by
      // its own (unreduced) index space, so the end is pushed out by that
      // count — otherwise the last page sits just past the furthest index the
      // drawn list can ever report, and its rows stay placeholders.
      onLoadRangeRef.current(startIndex, endIndex + omittedRef.current);
    };
    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [scroller, data.length, windowed]);

  // Keep the active row in view for KEYBOARD navigation only. Hovering must not
  // scroll: the row under the cursor is already visible, so pulling it "fully"
  // into view just slides the list, which lands a different row under a
  // stationary pointer — and that row may itself be clipped, cascading. It bit
  // hardest on the row clipped at the top edge, which is the first thing a user
  // reaches for after opening a list that starts scrolled to its selection.
  //
  // A cursor at rest on row 0 seats the scroller at its TOP, not the row at
  // the edge: the list's head — a selected section, the pool label — sits
  // above row 0, and "nearest" arriving from below would park it scrolled off.
  // That is where an open lands after the list changes under its first
  // scroll: the open seats the cursor on the selection while the pool still
  // lists it, scrolls there, and the row then leaves for the selected section.
  React.useEffect(() => {
    if (!activeId || isActiveFromPointer()) return;
    if (windowed) {
      // The active row may not be mounted: scroll by its slot instead.
      const slot = data.findIndex((item) => item?.id === activeId);
      if (slot >= 0) virtualizer.scrollToIndex(slot, { align: "auto" });
      return;
    }
    const el = rowsRef.current?.querySelector<HTMLElement>(
      `[data-entity-id="${activeId}"]`,
    );
    if (!el) return;
    const top = activeIndex === 0 ? el.closest<HTMLElement>("[data-slot=scroll-area-viewport]") : null;
    if (top) top.scrollTop = 0;
    else el.scrollIntoView({ block: "nearest" });
  }, [activeId, activeIndex, isActiveFromPointer, windowed, data, virtualizer]);

  // Items carrying `section` render their own in-list group headings, which
  // replace the single pool-label header — and, unless the pool names a rhythm,
  // set the roomier one, since rows packed as tightly as the group headings above
  // them stop reading as groups.
  const hasSections = data.some((item) => item?.section);
  const rowRhythm: EntitySelectorRowRhythm =
    rhythm ?? (hasSections ? "spacious" : "default");

  // A row's index in the KEYBOARD-NAV list, which the root builds from the
  // pinned rows followed by the visible ones. A row under a folded heading is
  // absent from it, so `index` cannot be the row's position in `data` — that
  // spelling drifted by one per hidden row and pointed hover at the wrong row.
  const navIndexById = React.useMemo(() => {
    const byId = new Map<string, number>();
    let next = pinnedItems.length;
    for (const item of data) {
      if (!item || isItemCollapsed(item, (key) => !!collapseApi?.isCollapsed(key)))
        continue;
      byId.set(item.id, next);
      next += 1;
    }
    return byId;
  }, [data, pinnedItems.length, collapseApi]);

  const renderRow = React.useCallback(
    (item: EntityItemConfig | null, index: number) => {
      if (!item || item.id === "__placeholder__") {
        return <PlaceholderRow height={windowRowHeight} />;
      }
      return (
        <EntitySelectorItem
          entity={item}
          poolId={value}
          actionLabel={badge?.label}
          actionTooltip={badge?.tooltip}
          onAction={handleItemAction}
          hideSelectionHighlight={hideSelectionHighlight}
          showCheckbox={!hideSelectionMarks}
          hideSelectedCheck={hideSelectionMarks}
          hideAvatar={hideAvatars}
          rhythm={rowRhythm}
          active={item.id === paintedActiveId}
          index={navIndexById.get(item.id) ?? pinnedItems.length + index}
          onActivate={setActiveIndex}
          grip={reorderable && item.reorderable !== false}
          gripStanding={gripStanding}
          gripGutter={reorderable}
          dimmed={item.id === draggingId}
        />
      );
    },
    [
      gripStanding,
      value,
      badge?.label,
      badge?.tooltip,
      handleItemAction,
      hideSelectionHighlight,
      hideSelectionMarks,
      hideAvatars,
      paintedActiveId,
      setActiveIndex,
      pinnedItems.length,
      navIndexById,
      rowRhythm,
      reorderable,
      draggingId,
      windowRowHeight,
    ],
  );

  // The bulk toggle reads the rows the viewer can see and choose: pinned plus
  // filtered, minus disabled. It follows the query, so a search narrows "all"
  // to the matches. Withheld on a capped or single-select selector, where the
  // verb could not be honoured.
  const selectableRows = React.useMemo(
    () =>
      selectAll && !config.maxSelections
        ? [...pinnedItems, ...filteredItems].filter((item) => !item.disabled)
        : EMPTY_ROWS,
    [selectAll, config.maxSelections, pinnedItems, filteredItems],
  );
  const allChosen =
    selectableRows.length > 0 && selectableRows.every((item) => isSelected(item.id));
  const selectAllAction = React.useMemo<
    EntitySelectorSectionLabelAction | undefined
  >(() => {
    if (selectableRows.length === 0) return undefined;
    return allChosen
      ? {
          label: "Clear",
          onClick: () => deselectItems(selectableRows.map((item) => item.id)),
        }
      : { label: "Select all", onClick: () => selectItems(selectableRows, value) };
  }, [selectableRows, allChosen, selectItems, deselectItems, value]);

  if (!isActive || !pool) {
    return null;
  }

  const SectionHeader =
    sectionLabel && !hasSections && !hideLabel ? (
      <EntitySelectorSectionLabel
        label={sectionLabel}
        action={labelAction ?? selectAllAction}
      />
    ) : null;

  // Their own lists: the pinned acts sit outside the scroller, so they cannot
  // share the scrolling list's element — and an option needs a list to
  // belong to. A sectioned run is headed and named by its section, never by
  // the label of the list below it.
  const PinnedRows = pinnedRuns.length ? (
    <>
      {pinnedRuns.map((run) => (
        <React.Fragment key={run.start}>
          {run.section ? (
            <Box paddingTop={run.start > 0 ? "xs" : undefined}>
              <EntitySelectorSectionLabel label={run.section} />
            </Box>
          ) : null}
          <Box
            role="listbox"
            aria-label={run.section ?? pool.pinnedLabel ?? sectionLabel}
            direction="column"
          >
            {run.items.map((item, offset) => (
              <EntitySelectorItem
                key={item.id}
                entity={item}
                poolId={value}
                hideAvatar={hideAvatars}
                hideSelectionHighlight={hideSelectionHighlight}
                rhythm={rowRhythm}
                active={item.id === paintedActiveId}
                index={run.start + offset}
                onActivate={setActiveIndex}
              />
            ))}
          </Box>
        </React.Fragment>
      ))}
    </>
  ) : null;

  if (data.length === 0) {
    // A label heading an empty list is worse than no label: it claims a section
    // exists and then shows nothing, which is what the empty state is for. It
    // stays only while loading, where it heads rows that are on their way.
    // Pinned rows that survived the filter (a Create offer for the typed name)
    // still render — they are acts, not members of the section, so they never
    // earn the label on their own.
    const loading = pool.loading === true;
    if (pinnedItems.length === 0 && !loading) return null;
    return (
      <Box
        data-slot="entity-selector-pool"
        // Same list-area gutter as the populated branch below.
        className="pt-1.5 pb-1.5 [[data-slot=entity-selector-selected]+&]:pt-0"
      >
        {PinnedRows}
        {loading ? SectionHeader : null}
      </Box>
    );
  }

  const rows = (
    // THE list. The role sits on the element that holds the option rows and
    // nothing else — a listbox may own only options and groups, and the
    // section header above it is a control (its bulk toggle is a real button),
    // never a member of the list it heads.
    <Box
      role="listbox"
      id="entity-selector-listbox"
      aria-label={sectionLabel}
      direction="column"
      position="relative"
      ref={rowsCallbackRef}
      style={windowed ? { height: virtualizer.getTotalSize() } : undefined}
    >
      {windowed
        ? virtualRows.map((row) => (
            <Box
              key={data[row.index]?.id ?? `placeholder-${row.index}`}
              position="absolute"
              className="left-0 right-0 top-0"
              style={{
                height: row.size,
                transform: `translateY(${row.start - scrollMargin}px)`,
              }}
            >
              {renderRow(data[row.index] ?? null, row.index)}
            </Box>
          ))
        : data.map((item, index) => {
        // Hide the ROWS of a folded section or subsection; the headings
        // themselves still render (they are the controls that reopen). Same
        // predicate the root filters the nav list with, so what is on screen and
        // what the cursor can reach cannot disagree.
        const hidden = isItemCollapsed(item, (key) => !!collapseApi?.isCollapsed(key));
        return (
          <React.Fragment key={item?.id ?? `placeholder-${index}`}>
            {sectionHeadingFor(
              data,
              index,
              collapseApi,
              pool.sectionHeadings,
              pool.sectionRules,
              pool.sectionTrailing,
            )}
            {hidden ? null : (
              // Subsection rows sit indented under their sub-heading so the
              // two levels read as a tree rather than a flat run.
              <Box paddingLeft={item?.subsection ? "sm" : undefined}>
                {renderRow(item, index)}
              </Box>
            )}
          </React.Fragment>
        );
      })}
      {/* Drop indicator — a hairline at the boundary the drag would land on.
          The dragged row itself dims via draggingId. */}
      {draggingId !== null && indicatorY !== null && (
        <Box
          aria-hidden
          position="absolute"
          className="pointer-events-none left-1.5 right-1.5 h-0.5 -translate-y-1/2 rounded-full bg-brand"
          style={{ top: indicatorY }}
        />
      )}
    </Box>
  );

  return (
    // The pool's frame: it holds the section heading, the pinned acts and the
    // scroller, and it is what keeps its height while results change inside
    // it. The list itself is the rows box below.
    <Box
      data-slot="entity-selector-pool"
      flex={1}
      minHeight={0}
      direction="column"
      // The list area's 6px gutter: below always (a fixed lane outside the
      // scroller, so the last row's wash never touches the popup corner), and
      // above only when this pool STARTS the list — a selected section
      // directly before it has already paid the top gutter.
      className="pt-1.5 pb-1.5 [[data-slot=entity-selector-selected]+&]:pt-0"
    >
      {PinnedRows}

      {ownScroller ? (
        // scrollbarGutter: the rows' trailing content (rowMeta, action
        // buttons) sits at the list's right edge, so an overlay scrollbar
        // rides on top of it — the gutter gives the bar its own lane.
        //
        // The cap is a fallback for a host that does not bound the list by
        // flex; inside `EntitySelectorContent` the pool frame shrinks to the
        // popup's available height, so this scroller is the popup's ONE.
        <ScrollArea
          scrollbarGutter={scrollbarGutter}
          style={{ maxHeight: POOL_SCROLL_CAP }}
        >
          <Box ref={selectedHostRef} data-slot="entity-selector-selected-host" />
          {SectionHeader}
          {rows}
        </ScrollArea>
      ) : (
        <>
          <Box ref={selectedHostRef} data-slot="entity-selector-selected-host" />
          {SectionHeader}
          {rows}
        </>
      )}
    </Box>
  );
}

// Never taller than the popup can show (`--available-height`, set by the
// hosting positioner), and never more than most of the screen where no
// positioner names one (a dialog body, a bare inline mount).
const POOL_SCROLL_CAP = "min(65vh, var(--available-height, 65vh))";

EntitySelectorPool.displayName = "EntitySelectorPool";

export { EntitySelectorPool, sectionHeadingFor };